<template>
  <div v-show="isOpening">

    <div class="mobile-booking-widget-backdrop" @click="hide"></div>

    <div class="mobile-booking-widget bg-white">
      <div class="dp-mobile-container h-16 flex mdsm:w-full sm:flex-none">
        <div class="flex items-center w-full">

          <template v-if="step === 1">
            <div class="flex-1 font-medium">
              <div class="text-black-light-5" v-html="dateDisplayValue"></div>
            </div>
          </template>
          <template v-else>
            <input type="text"
              class="flex-1 mobile-booking-widget-header font-medium"
              readonly
              :value="displayValue"
              :placeholder="titlePlaceholder" />
          </template>

          <button type="button" class="flex-none" @click="hide">
            <svg width="24" height="24">
              <use xlink:href="/img/sprite.svg#ic_close"></use>
            </svg>
          </button>
        </div>
      </div>

      <hr class="border-black-30" />
      <template v-if="step === 1">
        <single-calendar-select-list v-if="supplier.type === 'package'"
          class="dp-mobile-container py-5 dp-mobile-scroll-container overflow-x-hidden overflow-y-auto"
          v-model="value.date">
        </single-calendar-select-list>
        <range-calendar-select-list v-else-if="supplier.type === 'custom'"
          class="dp-mobile-container py-5 dp-mobile-scroll-container overflow-x-hidden overflow-y-auto"
          v-model="value.dates">
        </range-calendar-select-list>
      </template>
      <template v-else-if="step === 2">
        <ul class="w-full bg-white py-4 my-2 rounded select-list">
          <li class="py-2 cursor-pointer select-item"
            v-for="(option, index) in guests"
            :key="index"
            @click="handleSelectGuests(option)"
            :class="{ 'is-selected': isNumberOfGuestsSelected(option) }">
            {{ option.label }}
          </li>
        </ul>
      </template>
      <template v-else-if="step === 3 && supplier.type === 'package'">
        <ul class="w-full bg-white py-4 my-2 rounded select-list">
          <li class="py-2 cursor-pointer select-item"
            v-for="pkg in supplier.resortPackages"
            :key="pkg.value"
            @click="handleSelectPackage(pkg)"
            :class="{ 'is-selected': isPackageSelected(pkg) }">
            {{ pkg.label }}
          </li>
        </ul>
      </template>

      <hr class="border-black-30" />
      <div class="dp-mobile-container flex-none h-20 flex mdsm:w-full">
        <div class="flex m-auto w-full">
          <div class="flex-1 text-left">
            <button type="button" class="btn btn-outlined btn-tertiary input-tall"
              @click="handleCancelFlow">
              Cancel
            </button>
          </div>
          
          <div class="flex-none">
            <button type="button"
              class="btn btn-secondary input-tall"
              :disabled="!canProceed"
              @click="goToStep(step + 1)">
              <span v-if="step === 1">Select date</span>
              <span v-else-if="step === 2">Add guests</span>
              <span v-else-if="step === 3">Select package</span>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import CalendarHeader from '../core/DateRangePicker/CalendarHeader'
import CalendarGrid from '../core/DateRangePicker/CalendarGrid'
import GuestPicker from '../core/Booking/GuestPicker'
import PackagePicker from '../core/Booking/PackagePicker'
import SingleCalendarSelectList from '../core/Booking/SingleCalendarSelectList'
import RangeCalendarSelectList from '../core/Booking/RangeCalendarSelectList'
import {
  getBookingGuestsDisplay
} from '../../helpers'

export default {
  components: {
    CalendarHeader,
    CalendarGrid,
    GuestPicker,
    PackagePicker,
    SingleCalendarSelectList,
    RangeCalendarSelectList
  },
  props: ['value'],
  inject: ['supplier'],
  data () {
    return {
      isOpening: false,
      step: 1,
      displayingMonth: this.value.date ? this.value.date : moment()
    }
  },
  computed: {
    titlePlaceholder () {
      switch (this.step) {
        case 1: return 'Add travel dates'
        case 2: return 'Add guests'
        case 3: return 'Select package length'
        default: return ''
      }
    },
    displayValue () {
      switch (this.step) {
        case 1: return this.dateDisplayValue
        case 2: return getBookingGuestsDisplay(this.value.guests)
        case 3:
          const foundPackage = this.supplier.resortPackages.find(pkg => pkg.value === this.value.package)

          if (foundPackage) {
            return foundPackage.label
          }
        default: return ''
      }
    },
    dateDisplayValue () {
      if (this.value.date) {
        return this.value.date.format('MMM DD, YYYY')
      }

      return 'Add travel dates'
    },
    canProceed () {
      switch (this.step) {
        case 1:
          return this.supplier.type === 'custom'
            ? moment.isMoment(this.value.dates[0]) && moment.isMoment(this.value.dates[1])
            : moment.isMoment(this.value.date)
        case 2: return Number(this.value.guests) > 0 || this.value.guests === '10+'
        case 3: return true
        default: return false
      }
    },
    guests () {
      return [
        { value: '1', label: '1 guest' },
        { value: '2', label: '2 guests' },
        { value: '3', label: '3 guests' },
        { value: '4', label: '4 guests' },
        { value: '5', label: '5 guests' },
        { value: '6', label: '6 guests' },
        { value: '7', label: '7 guests' },
        { value: '8', label: '8 guests' },
        { value: '9', label: '9 guests' },
        { value: '10+', label: 'More than 10 guests' }
      ]
    }
  },
  methods: {
    show (step) {
      this.isOpening = true
      this.step = step
    },
    hide () {
      this.isOpening = false
    },
    handleDateClicked (date) {
      const formData = this.value
      formData.date = date
      this.$emit('input', formData)
    },
    goToStep (nextStep) {
      this.step = nextStep

      const maxStep = this.supplier.type === 'package' ? 4 : 3

      if (this.step === maxStep) {
        this.isOpening = false
      }
    },
    handleCancelFlow () {
      this.$emit('input', {
        date: null,
        guests: '0',
        package: '0' 
      })

      this.isOpening = false
    },
    handleSelectGuests (option) {
      const formData = this.value
      formData.guests = option.value
      this.$emit('input', formData)
    },
    isNumberOfGuestsSelected (option) {
      return option.value === this.value.guests
    },
    handleSelectPackage (pkg) {
      const formData = this.value
      formData.package = pkg.value
      this.$emit('input', formData)
    },
    isPackageSelected (pkg) {
      return pkg.value === this.value.package
    }
  },
  watch: {
    isOpening (value) {
      const openingModal = $('aside').is('.zb-modal.active')
      if (value) {
        window.toggleTrengoChat({ show: false })
      }
      else if(!openingModal) {
        window.toggleTrengoChat({ show: true })
      }
    }
  }
}
</script>

<style scoped>
@media (max-width: 1200px) {
  .select-list {
    margin: auto;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    max-width: 348px;
    height: 270px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .is-selected {
    background-color: var(--black-light-1);
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

@media (max-width: 600px) {
  .select-list {
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
